import type { Fraction } from '@mimir/common/util/fractionType';
import { approximateNumber, frac as _frac, simplifyFraction } from '@mimir/common/util/integerMath';
import { getSentry } from './sentryUtils';
import { inDevelopmentMode, logErrorAndShowToast } from './uiError';

export const frac = (numerator: number, denominator: number): Fraction => {
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator) || denominator === 0) {
    const sentry = getSentry();
    if (sentry) {
      sentry.captureException(new Error('frac called with unsafe integers'), {
        level: 'error',
        extra: {
          numerator,
          denominator,
        },
      });
    }
    if (inDevelopmentMode()) {
      logErrorAndShowToast(
        'ATTENTION DEVELOPER: frac called with unsafe integers. If accurate fraction is not required, please consider using safeFrac instead. If accurate fraction is required, please ensure values for numerator and denominator are not derived from unsafe sources such as html element dimensions.',
        new Error(`Numerator: ${numerator}, Denominator: ${denominator}`),
        'Please address this issue before deploying to production.',
        true
      );
    }
    return safeFrac(numerator, denominator);
  } else {
    return _frac(numerator, denominator);
  }
};

export const safeFrac = (numerator: number, denominator: number, nDecimalsToKeep: number = 3): Fraction => {
  if (denominator === 0) {
    throw new Error('Denominator cannot be zero');
  }
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator)) {
    return approximateNumber(numerator / denominator, nDecimalsToKeep);
  } else {
    return simplifyFraction(_frac(numerator, denominator));
  }
};
